import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api';

const POLL_INTERVAL = 2000; // 2 seconds, same as rationalization analysis

// ── File type icon helpers ────────────────────────────────────────

function typeIcon(ext) {
  if (!ext) return '📄';
  const e = ext.toLowerCase().replace('.', '');
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'tiff', 'tif', 'bmp', 'eps', 'ai', 'psd'].includes(e)) return '🖼';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'mxf'].includes(e)) return '🎬';
  if (['mp3', 'wav', 'aac', 'flac'].includes(e)) return '🎵';
  if (['pdf'].includes(e)) return '📕';
  if (['doc', 'docx'].includes(e)) return '📝';
  if (['xls', 'xlsx'].includes(e)) return '📊';
  if (['ppt', 'pptx'].includes(e)) return '📊';
  if (['zip', 'rar', '7z'].includes(e)) return '🗜';
  if (['indd', 'idml'].includes(e)) return '📐';
  return '📄';
}

// ── Relation type badge ───────────────────────────────────────────

function RelTypeBadge({ type }) {
  if (type === 'related') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
        ↔ Related
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
      ↳ Attachment
    </span>
  );
}

// ── Single relation group card ────────────────────────────────────

function GroupCard({ group, onConfirm, onDismiss, confirming, dismissing }) {
  const { group_id, relationship_type, reason, status, assets } = group;

  const isConfirmed = status === 'confirmed';
  const isDismissed = status === 'dismissed';
  const isSuggested = status === 'suggested';

  const cardBorder = isConfirmed
    ? 'border-green-200 bg-green-50'
    : isDismissed
    ? 'border-slate-200 bg-slate-50 opacity-60'
    : 'border-slate-200 bg-white hover:border-slate-300';

  return (
    <div className={`border rounded-lg p-4 transition ${cardBorder}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <RelTypeBadge type={relationship_type} />
          {isConfirmed && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
              ✓ Confirmed
            </span>
          )}
          {isDismissed && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-500">
              ✕ Dismissed
            </span>
          )}
        </div>
        {isSuggested && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => onConfirm(group_id)}
              disabled={confirming}
              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {confirming ? '…' : 'Confirm'}
            </button>
            <button
              onClick={() => onDismiss(group_id)}
              disabled={dismissing}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
            >
              {dismissing ? '…' : 'Dismiss'}
            </button>
          </div>
        )}
      </div>

      {/* Reason */}
      <p className="text-sm text-slate-600 mb-3 italic">"{reason}"</p>

      {/* Asset list */}
      <div className="space-y-1.5">
        {assets.map((asset, idx) => (
          <div key={asset.id} className="flex items-center gap-2 text-sm">
            {relationship_type === 'attachment' && (
              <span className="text-xs text-slate-400 w-10 flex-shrink-0 text-right">
                {idx === 0 ? 'parent' : 'child'}
              </span>
            )}
            <span className="flex-shrink-0">{typeIcon(asset.type)}</span>
            <span className="font-mono text-xs text-slate-700 break-all">{asset.filename}</span>
            {asset.folder && (
              <span className="text-xs text-slate-400 flex-shrink-0 hidden sm:inline">
                · {asset.folder}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Progress bar during analysis ──────────────────────────────────

function AnalysisProgress({ progress, onCancel }) {
  const { status, processed = 0, total = 0, message } = progress;

  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isStarting = status === 'starting';

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
      <div className="text-2xl mb-3">🔍</div>
      <div className="text-sm font-medium text-blue-800 mb-1">
        {isStarting ? 'Preparing analysis…' : message || `Analyzing assets for relationships…`}
      </div>
      {!isStarting && total > 0 && (
        <div className="text-xs text-blue-600 mb-3">
          {processed} of {total} assets processed
        </div>
      )}
      <div className="w-full bg-blue-100 rounded-full h-2 mb-4">
        <div
          className="bg-blue-500 h-2 rounded-full transition-all duration-500"
          style={{ width: isStarting ? '5%' : `${Math.max(pct, 5)}%` }}
        />
      </div>
      <button
        onClick={onCancel}
        className="px-3 py-1.5 text-xs font-medium text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 transition"
      >
        Cancel
      </button>
      <p className="text-xs text-blue-500 mt-3">
        Analysis runs in the background — you can navigate away and return.
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────

export default function RelationsAuthoring() {
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState({ status: 'idle' });
  const [analysing, setAnalysing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('suggested'); // 'suggested' | 'confirmed' | 'dismissed' | 'all'
  const [bulkModal, setBulkModal] = useState(null); // null | { action: 'accept' | 'dismiss', count: N }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);

  const pollRef = useRef(null);

  // ── Polling ────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await apiFetch(`/relations/summary`);
      if (!res.ok) return;
      const data = await res.json();
      setSummary(data);
    } catch (_) {
      // Non-fatal — summary will reload next poll or on next mount
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/relations/analyse/progress`);
        if (!res.ok) return;
        const data = await res.json();
        setProgress(data);

        if (data.status === 'complete' || data.status === 'error' ||
            data.status === 'cancelled') {
          stopPolling();
          setAnalysing(false);
          loadSummary();
        }
      } catch (_) {
        // Network hiccup — keep polling
      }
    }, POLL_INTERVAL);
  }, [stopPolling, loadSummary]);

  // ── Mount: load summary and check if analysis is already running ──

  useEffect(() => {
    const init = async () => {
      try {
        // Load current summary
        await loadSummary();
        const statusResponse = await apiFetch(`/ai/status`);
        if (statusResponse.ok) {
          const status = await statusResponse.json();
          setAiAvailable(Boolean(status.configured));
        }

        // Check if analysis was already running (e.g. navigated away and back)
        const res = await apiFetch(`/relations/analyse/progress`);
        if (res.ok) {
          const data = await res.json();
          setProgress(data);
          if (data.status === 'running' || data.status === 'starting') {
            setAnalysing(true);
            startPolling();
          }
        }
      } catch (_) {
        // Ignore
      } finally {
        setLoading(false);
      }
    };
    init();

    return () => stopPolling();
  }, [loadSummary, startPolling, stopPolling]);

  // ── Actions ────────────────────────────────────────────────────

  const handleStartAnalysis = async () => {
    setError(null);
    setApplyResult(null);
    try {
      const res = await apiFetch(`/relations/analyse`, { method: 'POST' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.message || data.error);
      setAnalysing(true);
      setProgress({ status: 'starting', processed: 0, total: 0 });
      startPolling();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = async () => {
    try {
      await apiFetch(`/relations/analyse/cancel`, { method: 'POST' });
    } catch (_) {
      // Ignore
    }
  };

  const handleConfirm = async (groupId) => {
    setConfirmingId(groupId);
    try {
      const res = await apiFetch(`/relations/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupId }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      // Reload summary to reflect updated counts
      await loadSummary();
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDismiss = async (groupId) => {
    setDismissingId(groupId);
    try {
      const res = await apiFetch(`/relations/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupId }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      await loadSummary();
    } catch (err) {
      setError(err.message);
    } finally {
      setDismissingId(null);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setApplyResult(null);
    setError(null);
    try {
      const res = await apiFetch(`/relations/apply`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || `Server returned ${res.status}`);
      }
      const data = await res.json();
      setApplyResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkModal) return;
    const endpoint = bulkModal.action === 'accept' ? 'confirm-all' : 'dismiss-all';
    setBulkBusy(true);
    try {
      const res = await apiFetch(`/relations/${endpoint}`, { method: 'POST' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.message || data.error);
      setBulkModal(null);
      await loadSummary();
      // Switch to the appropriate tab to show the result
      setActiveFilter(bulkModal.action === 'accept' ? 'confirmed' : 'dismissed');
    } catch (err) {
      setError(err.message);
      setBulkModal(null);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleResetAnalysis = async () => {
    if (!window.confirm('Clear all relation suggestions and start over?')) return;
    try {
      await apiFetch(`/relations/reset`, { method: 'POST' });
      setProgress({ status: 'idle' });
      setSummary(null);
      setApplyResult(null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Derived state ──────────────────────────────────────────────

  const groups = summary?.groups || [];
  const suggestedGroups = groups.filter(g => g.status === 'suggested');
  const confirmedGroups = groups.filter(g => g.status === 'confirmed');
  const dismissedGroups = groups.filter(g => g.status === 'dismissed');

  const filteredGroups = activeFilter === 'suggested' ? suggestedGroups
    : activeFilter === 'confirmed' ? confirmedGroups
    : activeFilter === 'dismissed' ? dismissedGroups
    : groups;

  // ── Loading ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="text-center py-16 text-slate-500 text-sm">
        Loading relations data…
      </div>
    );
  }

  // ── Analysis in progress ────────────────────────────────────────

  if (analysing) {
    return (
      <div className="max-w-2xl mx-auto">
        <SectionHeader />
        <AnalysisProgress progress={progress} onCancel={handleCancel} />
      </div>
    );
  }

  // ── Error from progress ────────────────────────────────────────

  if (progress.status === 'error' && !summary?.has_analysis) {
    return (
      <div className="max-w-2xl mx-auto">
        <SectionHeader />
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          <div className="font-semibold mb-1">Analysis failed</div>
          <div>{progress.message}</div>
          <button
            onClick={handleStartAnalysis}
            className="mt-3 px-4 py-2 text-xs font-medium text-white bg-slate-700 rounded-lg hover:bg-slate-800 transition"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ── No analysis run yet ────────────────────────────────────────

  if (!summary?.has_analysis) {
    return (
      <div className="max-w-2xl mx-auto">
        <SectionHeader />
        {error && <ErrorBanner message={error} />}
        <IdleState onStart={handleStartAnalysis} aiAvailable={aiAvailable} />
      </div>
    );
  }

  // ── Results view ───────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Relations Authoring</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {summary.keep_asset_count} Keep assets analyzed ·{' '}
            {summary.suggested_count} suggested ·{' '}
            {summary.confirmed_count} confirmed ·{' '}
            {summary.dismissed_count} dismissed
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleResetAnalysis}
            disabled={!aiAvailable}
            title={!aiAvailable ? 'AI is not configured — contact your administrator' : ''}
            className="px-3 py-1.5 text-xs font-medium text-slate-500 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Re-analyze
          </button>
          {suggestedGroups.length > 0 && (
            <>
              <button
                onClick={() => setBulkModal({ action: 'dismiss', count: suggestedGroups.length })}
                disabled={bulkBusy}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
              >
                Dismiss all ({suggestedGroups.length})
              </button>
              <button
                onClick={() => setBulkModal({ action: 'accept', count: suggestedGroups.length })}
                disabled={bulkBusy}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                Accept all ({suggestedGroups.length})
              </button>
            </>
          )}
          <button
            onClick={handleApply}
            disabled={applying || summary.confirmed_count === 0}
            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-40"
          >
            {applying ? 'Applying…' : `Apply ${summary.confirmed_count} confirmed →`}
          </button>
        </div>
      </div>

      {/* Apply result banner */}
      {applyResult && !applyResult.error && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700">
          Relations applied — {applyResult.confirmed_group_count} group{applyResult.confirmed_group_count !== 1 ? 's' : ''} written to the working CSV.
          The Export phase will include Related_IDs and Attached_to_IDs columns automatically.
        </div>
      )}

      {/* Error banner */}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {/* Cancelled notice */}
      {progress.status === 'cancelled' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
          Analysis canceled. {groups.length > 0 ? 'Showing groups from the last completed batch.' : ''}
          <button
            onClick={handleStartAnalysis}
            className="ml-3 underline text-amber-800 hover:no-underline"
          >
            Run again
          </button>
        </div>
      )}

      {/* Skip notice */}
      {groups.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
          <div className="text-2xl mb-2">✓</div>
          No probable relationships were detected in this asset set.
          <br />
          You can proceed to Export — Relations columns will be omitted.
        </div>
      ) : (
        <>
          {/* Filter tabs */}
          <div className="flex gap-1 border-b border-slate-200 pb-0">
            {[
              { key: 'suggested', label: `Pending (${suggestedGroups.length})` },
              { key: 'confirmed', label: `Confirmed (${confirmedGroups.length})` },
              { key: 'dismissed', label: `Dismissed (${dismissedGroups.length})` },
              { key: 'all', label: `All (${groups.length})` },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                  activeFilter === tab.key
                    ? 'border-blue-500 text-blue-700 bg-blue-50'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Group cards */}
          {filteredGroups.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">
              No {activeFilter !== 'all' ? activeFilter : ''} groups.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredGroups.map(group => (
                <GroupCard
                  key={group.group_id}
                  group={group}
                  onConfirm={handleConfirm}
                  onDismiss={handleDismiss}
                  confirming={confirmingId === group.group_id}
                  dismissing={dismissingId === group.group_id}
                />
              ))}
            </div>
          )}

          {/* Skip notice at bottom */}
          <div className="text-xs text-slate-400 text-center pt-2">
            Relations are optional in Frontify. Skipping this step produces no change to the export —
            proceed to Export whenever ready.
          </div>
        </>
      )}

      {/* Bulk action confirmation modal */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">
              {bulkModal.action === 'accept'
                ? `Accept all ${bulkModal.count} relation group${bulkModal.count !== 1 ? 's' : ''}?`
                : `Dismiss all ${bulkModal.count} relation group${bulkModal.count !== 1 ? 's' : ''}?`}
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              {bulkModal.action === 'accept'
                ? `This will confirm all ${bulkModal.count} suggested group${bulkModal.count !== 1 ? 's' : ''}. Related_IDs and Attached_to_IDs will be written to your working file when you click Apply.`
                : `This will dismiss all ${bulkModal.count} suggested group${bulkModal.count !== 1 ? 's' : ''}. Dismissed groups can be seen in the Dismissed tab but will not be exported. You can re-analyze to start over.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setBulkModal(null)}
                disabled={bulkBusy}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAction}
                disabled={bulkBusy}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50 ${
                  bulkModal.action === 'accept'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-slate-600 hover:bg-slate-700'
                }`}
              >
                {bulkBusy
                  ? (bulkModal.action === 'accept' ? 'Accepting…' : 'Dismissing…')
                  : (bulkModal.action === 'accept'
                      ? `Accept all ${bulkModal.count}`
                      : `Dismiss all ${bulkModal.count}`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

function SectionHeader() {
  return (
    <div className="mb-6">
      <h2 className="text-base font-semibold text-slate-800">Relations Authoring</h2>
      <p className="text-sm text-slate-500 mt-0.5">
        Analyze your Keep assets to identify probable relationships — color-space variants,
        format variants, source/final pairs — before export.
      </p>
    </div>
  );
}

function IdleState({ onStart, aiAvailable }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
      <div className="text-3xl mb-4">🔗</div>
      <h3 className="text-sm font-semibold text-slate-800 mb-2">
        Analyze asset relationships
      </h3>
      <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
        AI will scan your Keep assets for probable variants (e.g. RGB / CMYK versions
        of a logo) and parent/child pairs (e.g. an InDesign file and its exported PDF).
        Each suggestion requires your confirmation before anything is written.
      </p>
      <button
        onClick={onStart}
        disabled={!aiAvailable}
        title={!aiAvailable ? 'AI is not configured — contact your administrator' : ''}
        className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed"
      >
        Analyze relationships →
      </button>
      <p className="text-xs text-slate-400 mt-4">
        {aiAvailable
          ? 'This step is optional. You can skip to Export at any time.'
          : 'AI is not configured. You can skip this optional step.'}
      </p>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-start justify-between gap-3">
      <span>{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-red-500 hover:text-red-700 flex-shrink-0 text-xs">
          ✕
        </button>
      )}
    </div>
  );
}
